import * as React from 'react'
import type { IconProps } from '@grammarly/design-system'
import { Icon, LogoLogomarkColorDefaultIcon } from '@grammarly/design-system'

import styles from './styles.module.scss'
import { useEdgeDrag } from '../hooks/useEdgeDrag'
import { type MousemoveTarget, useHiddenPercent } from '../hooks/useHiddenPercent'
import { NotchState } from '../constants'
import { useCustomEnterLeaveMouseEvents } from './useCustomEnterLeaveMouseEvents'

export type Edge = 'left' | 'right'

/**
 * Interface supporting props for the assistant notch
 */
export interface AssistantNotchProps {
  readonly alwaysVisible?: boolean
  readonly onClick?: () => void
  readonly onMouseEnter?: () => void
  readonly onMouseLeave?: () => void
  // call each time, when the AssistantNotch changes it's position during dragging
  readonly onNotchPositionChanged?: (position: {
    top: number
    left: number
    width: number
    height: number
  }) => void
  // the icon to display in the notch, defaults to LogoLogomarkColorDefaultIcon
  readonly icon?: IconProps['icon']
  // the target to watch for mousemove events, defaults to document
  readonly mousemoveTarget?: MousemoveTarget
  // optional, which edge to restrict draggable notch to - defaults to 'right' if not specified
  readonly clampEdge?: Edge
  // optional, if true, the notch will not be draggable
  readonly isDraggingDisabled?: boolean
  // optional, called when the dragging is changed
  readonly onDraggingChanged?: (isDragging: boolean) => void
  // optional, if true, the notch will not have a shadow
  readonly isShadowDisabled?: boolean
}

/**
 * Assistant Notch is an entry point for the user to display the
 * always-available-assistant
 */
export const AssistantNotch = ({
  alwaysVisible,
  onClick,
  icon = LogoLogomarkColorDefaultIcon,
  mousemoveTarget,
  onMouseEnter,
  onMouseLeave,
  onNotchPositionChanged,
  clampEdge = 'right',
  isDraggingDisabled,
  onDraggingChanged,
  isShadowDisabled,
}: AssistantNotchProps) => {
  const notchRef = React.useRef<HTMLDivElement | null>(null)
  const { hiddenPercent } = useHiddenPercent({
    notchRef,
    mousemoveTarget,
    clampEdge,
  })
  const [notchState, setNotchState] = React.useState<NotchState>(NotchState.peeking)

  const NOTCH_HEIGHT = 78 // defined in styles.module.scss
  const NOTCH_WIDTH = 28 // defined in styles.module.scss

  const {
    style: draggingStyle,
    onMouseDown,
    isDragging,
  } = useEdgeDrag({
    clampToEdge: clampEdge,
    storageKey: `AssistantNotch-position-${clampEdge}`,
    elementSize: { width: NOTCH_WIDTH, height: NOTCH_HEIGHT },
    isDraggingDisabled,
    isShadowDisabled,
    onNotchPositionChanged,
  })

  React.useEffect(() => {
    onDraggingChanged?.(isDragging)
  }, [isDragging, onDraggingChanged])

  const finalNotchState = getFinalNotchState(
    notchState,
    hiddenPercent,
    alwaysVisible === true,
    isDragging,
  )

  const combinedStyle = {
    transform:
      finalNotchState === NotchState.shown
        ? undefined
        : `translateX(${clampEdge === 'right' ? '' : '-'}${hiddenPercent}%)`,
    ...draggingStyle,
  }

  useCustomEnterLeaveMouseEvents(
    notchRef,
    () => {
      setNotchState(NotchState.shown)
      onMouseEnter?.()
    },
    () => {
      setNotchState(NotchState.peeking)
      onMouseLeave?.()
    },
    mousemoveTarget,
  )

  return (
    <div
      data-testid="assistant-notch"
      ref={notchRef}
      className={`${styles.notch} ${styles[finalNotchState]} ${isShadowDisabled ? styles.noShadow : ''} ${styles[`${clampEdge}Edge`]}`}
      style={combinedStyle}
    >
      <button
        onClick={event => {
          if (!isDragging) {
            onClick?.()

            // explicitly remove focus on click - the css style 'focus-within' overrides transform
            // to none, and when the Assistant opens and Notch is hidden, focus isn't lost which caused
            // the Notch to be fully visible until a new mouse event is detected
            event.currentTarget.blur()
          }
        }}
        onMouseDown={onMouseDown}
        className={styles.notchButton}
        data-testid="assistant-notch-button"
        aria-label="Start a chat with Grammarly AI"
      >
        <Icon accessibilityLabel="" icon={icon} size="small" />
      </button>
    </div>
  )
}

/**
 * Get the notch state based on the notch state, and the hidden percent.
 * and a peeking notch with hiddenPercent === 100, the state will be hidden.
 * @param notchState current notch state
 * @param hiddenPercent hidden percent
 * @param alwaysVisible notch should always be shown
 * @param isDragging drag in progress, keep notch fully shown
 * @returns notch state
 */
function getFinalNotchState(
  notchState: NotchState,
  hiddenPercent: number,
  alwaysVisible: boolean,
  isDragging: boolean,
) {
  if (alwaysVisible || isDragging) return NotchState.shown

  if (notchState === NotchState.peeking && hiddenPercent === 100) {
    return NotchState.hidden
  }

  return notchState
}
